import { Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { MenuScopeService } from '../../shared/services/menu-scope.service';
import { AppsService } from '../../shared/services/apps.service';
import { environment } from '../../../environments/environment';

/**
 * MenuScopeGuard — refuses navigation to a screen the user's APP PROFILE does not
 * grant, and (when enforcing) logs the session out.
 *
 * SECURITY (audit obs #2 — Privilege Escalation, CWE-269).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * READ THIS BEFORE CITING THIS GUARD AS A FIX
 * ─────────────────────────────────────────────────────────────────────────────
 * This closes the NAVIGATION half of the audited finding: a State user pasting
 * /internal/ams_vehicles/add_newvehcile no longer lands on Manage Vehicle.
 *
 * It does NOT close the finding. The audit's step 4 is "fill in the details and
 * click Add", and that is an API call — POST /apiv1/drivers/vehicles/Master/Device/assign.
 * Anyone can make it with curl and a valid token, without ever loading Angular.
 * This guard is not in that path and never will be.
 *
 * The control that refuses the write is server-side and already wired:
 *   checkUser.hasToken + checkUser.vldInsert('assetTracking')  (driverRtr.js)
 * — and, as measured on 2026-08-17, it currently passes for the audited account
 * because the "Audit" role holds insrt_in on assetTracking. Fixing THAT closes the
 * finding. This guard makes the UI honest; it does not make the API safe.
 *
 * Adding a screen here without a matching server-side vld*(object) gate re-creates
 * the audited vulnerability behind a friendlier UI.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FORCED LOGOUT — requested behaviour, and its cost
 * ─────────────────────────────────────────────────────────────────────────────
 * On an out-of-profile navigation this ends the session: server logout, clear
 * local storage, back to /internal.
 *
 * It is worth being clear that logging out stops nothing an attacker does — the
 * API call succeeds regardless — while it does cost honest users: a stale
 * bookmark, a shared deep link, an old browser tab or a mistyped URL ends a
 * session mid-task, losing unsaved work. A redirect to the user's home dashboard
 * achieves the same intent without that failure mode.
 *
 * It is implemented as asked, but STAGED, because the blast radius is the whole
 * user base:
 *
 *   menuScope.mode = 'off'      (default) do nothing at all
 *                    'report'   allow, and console.warn what WOULD have happened
 *                    'redirect' block and send to the home dashboard — no logout
 *                    'logout'   block and end the session   ← as requested
 *
 * Set it in environment.ts. Start at 'report', read the warnings for a release,
 * then move up. Every other control in this remediation was staged the same way,
 * for the same reason: the first version of a rule is always wrong about some real
 * user.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FAIL OPEN, DELIBERATELY
 * ─────────────────────────────────────────────────────────────────────────────
 * If the menu cannot be resolved — endpoint down, or the user simply has no app
 * profile — this ALLOWS. Measured on production: 1,480 of 6,889 active
 * usr_clnt_tnt_rel_t rows (21%) have no app_prfle_id, so they resolve to an empty
 * menu. Denying on "unknown" would log a fifth of the user base out on their first
 * click, while closing nothing. Unknown is not the same as forbidden.
 */
@Injectable({ providedIn: 'root' })
export class MenuScopeGuard {

  /**
   * From environment.ts — see the block comment above. Defaults to 'report' if the
   * key is absent, so an environment file that predates this guard cannot silently
   * start ending sessions.
   */
  private readonly mode: 'off' | 'report' | 'redirect' | 'logout' =
    (environment as any).menuScope?.mode || 'report';

  constructor(
    private router: Router,
    private menuScope: MenuScopeService,
    private apiSrv: AppsService
  ) { }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    if (this.mode === 'off') { return of(true); }

    const target = state.url;

    return this.menuScope.load().pipe(
      map(() => {
        if (this.menuScope.isPermitted(target)) { return true; }

        const granted = this.menuScope.current || [];
        const msg = '[menu-scope] "' + target + '" is not granted by this app profile. '
          + 'Granted: ' + JSON.stringify(granted);

        if (this.mode === 'report') {
          // Log-only: prove the rule against real traffic before it can end sessions.
          console.warn(msg + ' — mode=report, allowing.');
          return true;
        }

        console.warn(msg + ' — mode=' + this.mode + ', blocking.');

        if (this.mode === 'logout') { this.forceLogout(); }
        else { this.router.navigate([this.safeFallback(target)]); }

        return false;
      }),
      // Never let a guard failure become a lockout.
      catchError(() => of(true))
    );
  }

  /**
   * Where to send a user whose navigation was refused — WITHOUT creating a loop.
   *
   * `homeUrl` is `hmdshb_pg` from the login response (login.ts:149). That is a
   * DIFFERENT source from the app-profile menu this guard checks
   * (usr_clnt_tnt_rel_t.app_prfle_id → app_lst_t.app_url_tx), so the two can
   * disagree: a user's home dashboard is not guaranteed to be a screen their app
   * profile grants.
   *
   * When they disagree, redirecting to `homeUrl` re-enters this guard, is refused
   * again, and redirects to `homeUrl` again — an infinite navigation loop that
   * pins the tab. In 'report' mode this could not happen (nothing was ever
   * blocked), so the risk arrives with the dial, not before it.
   *
   * Two conditions are checked before trusting `homeUrl`:
   *   - it is not the route we just refused (self-redirect), and
   *   - the profile actually permits it.
   * Otherwise fall back to '/internal/dashboard', which is in
   * MenuScopeService.ALWAYS_ALLOWED and therefore cannot be refused by this guard.
   */
  private safeFallback(refused: string): string {
    const home = localStorage.getItem('homeUrl');
    if (!home) { return '/internal/dashboard'; }

    const sameAsRefused =
      MenuScopeService.normalise(home) === MenuScopeService.normalise(refused);

    if (sameAsRefused || !this.menuScope.isPermitted(home)) {
      console.warn('[menu-scope] homeUrl "' + home + '" is itself not permitted '
        + '(or is the refused route) — falling back to /internal/dashboard to avoid a redirect loop.');
      return '/internal/dashboard';
    }
    return home;
  }

  /**
   * End the session the same way the header and side-nav do, so there is one
   * logout behaviour in the app rather than a second, subtly different one.
   * Local state is cleared and the user is returned to /internal even if the
   * server call fails — otherwise a network error would leave them signed in on a
   * screen this guard just refused.
   */
  private forceLogout(): void {
    const finish = () => {
      localStorage.clear();
      this.router.navigate(['/internal'], { replaceUrl: true });
    };
    try {
      this.apiSrv.get('auth/logout').subscribe(finish, finish);
    } catch {
      finish();
    }
  }
}
